import clsx from "clsx";
import React, { type InputHTMLAttributes, type JSX } from "react";
import Select, { type SelectItemRenderer, type SelectRenderer } from "react-dropdown-select";

import { InputError } from "../InputError";

import "./styles.css";
import { LoadingSpinner } from "shared-components/components/LoadingSpinner";

interface Option {
  id: string;
  value: string;
  label: string;
}

interface Props extends InputHTMLAttributes<HTMLSelectElement> {
  field: string;
  options: Option[];
  disabled?: boolean;
  hideBorder?: boolean;
  selectClassName?: string;
  errorMessage?: string;
  label?: string;
  withSearch?: boolean;
  loading?: boolean;
  noDataLabel?: string;
  multi?: boolean;
  initialvalue?: string;
  // The renderers are needed when we want to render a initial value and the options'
  // label are custom renders instead of simple strings, else the component will break
  contentRenderer?: (args: SelectRenderer<Option>) => JSX.Element;
  itemRenderer?: (args: SelectItemRenderer<Option>) => JSX.Element;
}

export const InputSelect = ({
  disabled,
  hideBorder,
  errorMessage,
  label,
  options,
  onChange,
  className,
  selectClassName,
  withSearch = false,
  placeholder = "Selecione uma opção",
  loading = false,
  noDataLabel = "No data",
  initialvalue,
  multi,
  contentRenderer,
  itemRenderer,
}: Props): JSX.Element => {
  const initialSelectedOptions =
    initialvalue !== undefined ? options.filter(({ value }) => value === initialvalue) : undefined;

  return (
    <div className={clsx("system-input-select", className)}>
      {label && (
        <label className={clsx("mb-1 block lg:mb-0.5", disabled && "opacity-50")}>
          <b>{label}</b>
        </label>
      )}

      <Select
        className={clsx("!h-auto", selectClassName, {
          searchable: withSearch,
          error: errorMessage,
          disabled: loading || disabled,
          "!outline-none": hideBorder,
        })}
        options={options}
        onSelect={(values) => {
          if (onChange) {
            onChange({
              target: { value: values.length ? values[0].value : "" },
            } as React.ChangeEvent<HTMLSelectElement>);
          }
        }}
        placeholder={placeholder}
        searchable={withSearch}
        // @ts-expect-error the package has the wrong typing, it should allow undefined value
        values={initialSelectedOptions}
        disabled={disabled || loading}
        multi={multi}
        contentRenderer={!loading ? contentRenderer : () => <span></span>}
        itemRenderer={itemRenderer}
        loading={loading}
        noDataLabel={noDataLabel}
        loadingRenderer={() => <LoadingSpinner />}
      />

      <InputError message={errorMessage} />
    </div>
  );
};
